import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { QuestionDto, QuestionSetDto } from 'ts-types/api.types';
import axios, { CancelTokenSource } from 'axios';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import { Button, Grid, Header, Icon } from 'semantic-ui-react';
import Input from 'components/final-form/Input';
import { required } from 'utils/validatorUtils';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import { addQuestionSet, editQuestionSet } from 'services/questionSetsServices';
import { FieldArray } from 'react-final-form-arrays';
import Select from 'components/final-form/Select';
import arrayMutators from 'final-form-arrays';
import styled from 'styled-components';
import PraxFormScrollableContainer from 'components/layout/PraxFormScrollableContainer';
import CheckBox from 'components/final-form/CheckBox';
import { FormApi } from 'final-form';

interface Props extends MessageSourceProps {
  toggleAddEditModal: () => void,
  onCancel: () => void,
  onSave: (questionSetDto: QuestionSetDto) => void,
  questionSetId?: number,
  questionSet?: QuestionSetDto
}

interface State {
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource,
  questionSet?: QuestionSetDto
}

const StyledIcon = styled(Icon)`
  margin-right: 0 !important;
`;

const StyledCheckBoxContainer = styled.div`
  //margin-bottom: 0.25rem;
`

class UpsertQuestionSetForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);
    const cancelTokenSource = axios.CancelToken.source();
    this.state = {
      cancelTokenSource
    }
  }

  handleSubmit = async (values: Partial<QuestionSetDto>) => {

    let saveValues: Partial<QuestionSetDto> = {...values};

    const {questionSetId, getMessage, onSave} = this.props;
    const {cancelTokenSource} = this.state;

    let questions = saveValues.questions;
    if (questions) {
      questions.forEach((q, index) => {
          q.questionOrder = index + 1;
      })
    }

    const handleError = () => {
      this.setState({
        errorMessage: getMessage('error.general')
      })
    };

    if (questionSetId) {
      await editQuestionSet(questionSetId, saveValues, cancelTokenSource)
          .then(editedQuestionSet => onSave(editedQuestionSet))
          .catch(handleError)
    } else {
      await addQuestionSet(saveValues, cancelTokenSource)
          .then(newQuestionSet => onSave(newQuestionSet))
          .catch(handleError)
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  static getDerivedStateFromProps(props: Props) {
    return {
      questionSet: props.questionSet
    }
  }

  onUpArrowClick = (form: FormApi, index: number) => {
    if (index === 0) {
      return;
    }

    const values = form.getState().values;
    let currentValues: Partial<QuestionSetDto> = {...values};

    if (currentValues.questions) {
      const currentQuestion = {...currentValues.questions[index]}
      const previousQuestion = {...currentValues.questions[index - 1]}

      form.change(`questions[${index}]`, previousQuestion)
      form.change(`questions[${index - 1}]`, currentQuestion)
    }

  }

  onDownArrowClick = (form: FormApi, index: number) => {
    const values = form.getState().values;
    let currentValues: Partial<QuestionSetDto> = {...values};

    if (currentValues.questions) {
      if (currentValues.questions.length === index + 1) {
        return;
      }
      const currentQuestion = {...currentValues.questions[index]}
      const nextQuestion = {...currentValues.questions[index + 1]}

      form.change(`questions[${index}]`, nextQuestion)
      form.change(`questions[${index + 1}]`, currentQuestion)
    }

  }

  render() {

    const defaultValues: Partial<QuestionSetDto> = {
      name: '',
      questions: [],
      active: true
    };

    const {questionSet = defaultValues} = this.state;

    return (
        <FinalForm
            onSubmit={(values => this.handleSubmit(values))}
            initialValues={questionSet}
            subscription={{pristine: true, submitting: true}}
            mutators={{
              ...arrayMutators
            }}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting, form}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {errorMessage} = this.state;

    const questionTypes: Array<String> = ["SINGLE_ANSWER", "MULTI_ANSWER", "FREE_TEXT", "YES_NO"];
    const questionTypeOptions = questionTypes.map(questionType => ({
      id: questionType,
      text: getMessage(`questionSet.questions.questionType.${questionType}`),
      value: questionType
    }));

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
              }
              <PraxFormScrollableContainer height={"480px"}>
                <PraxFormGrid>
                  <Grid.Row>
                    <Grid.Column width={16} textAlign="right">
                      <StyledCheckBoxContainer>
                        <Field
                            name="active"
                            component={CheckBox}
                            label={getMessage("guarantor.active")}
                            toggle
                        />
                      </StyledCheckBoxContainer>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5}>
                      {getMessage('questionSet.name')}
                    </Grid.Column>
                    <Grid.Column width={11}>
                      <Field
                          fluid
                          name="name"
                          component={Input}
                          validate={required}
                          autoFocus
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5}>
                      {getMessage('questionSet.nameEn')}
                    </Grid.Column>
                    <Grid.Column width={11}>
                      <Field
                          fluid
                          name="nameEn"
                          component={Input}
                          autoFocus
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5}>
                      {getMessage('questionSet.nameFr')}
                    </Grid.Column>
                    <Grid.Column width={11}>
                      <Field
                          fluid
                          name="nameFr"
                          component={Input}
                          autoFocus
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5}>
                      {getMessage('questionSet.nameIt')}
                    </Grid.Column>
                    <Grid.Column width={11}>
                      <Field
                          fluid
                          name="nameIt"
                          component={Input}
                          autoFocus
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={3}>
                      <Header size="small">{getMessage('questionSet.questions.header')}</Header>
                    </Grid.Column>
                  </Grid.Row>
                  <FieldArray name="questions">
                    {
                      ({fields}) => {
                        const questionsForm = fields.map((name, index) => {
                          const currentQuestion = fields.value[index];
                          return (
                              <React.Fragment key={index}>
                                <Grid.Row>
                                  <Grid.Column width={2}>
                                    <Header size="medium" style={{display: "inline-flex"}}>
                                      {index + 1}.
                                      <Icon name={'arrow alternate circle up outline'} size={'small'}
                                            style={{marginRight: "0.1rem", marginLeft: "0.4rem"}}
                                            disabled={index === 0}
                                            onClick={() => this.onUpArrowClick(form, index)}
                                      />
                                      &nbsp;
                                      <Icon name={'arrow alternate circle down outline'} size={'small'}
                                            disabled={index + 1 === fields.length}
                                            onClick={() => this.onDownArrowClick(form, index)}
                                      />
                                    </Header>
                                  </Grid.Column>
                                  <Grid.Column width={3}>
                                    {getMessage('questionSet.questions.text')}
                                  </Grid.Column>
                                  <Grid.Column width={11}>
                                    <Field
                                        name={`${name}.text`}
                                        component={Input}
                                        validate={required}
                                        placeholder={getMessage('questionSet.questions.text.placeholder')}
                                        fluid
                                    />
                                  </Grid.Column>
                                </Grid.Row>
                                <Grid.Row>
                                  <Grid.Column width={2}></Grid.Column>
                                  <Grid.Column width={3}>
                                    {getMessage('questionSet.questions.textEn')}
                                  </Grid.Column>
                                  <Grid.Column width={11}>
                                    <Field
                                        name={`${name}.textEn`}
                                        component={Input}
                                        placeholder={getMessage('questionSet.questions.text.placeholder')}
                                        fluid
                                    />
                                  </Grid.Column>
                                </Grid.Row>
                                <Grid.Row>
                                  <Grid.Column width={2}></Grid.Column>
                                  <Grid.Column width={3}>
                                    {getMessage('questionSet.questions.textFr')}
                                  </Grid.Column>
                                  <Grid.Column width={11}>
                                    <Field
                                        name={`${name}.textFr`}
                                        component={Input}
                                        placeholder={getMessage('questionSet.questions.text.placeholder')}
                                        fluid
                                    />
                                  </Grid.Column>
                                </Grid.Row>
                                <Grid.Row>
                                  <Grid.Column width={2}></Grid.Column>
                                  <Grid.Column width={3}>
                                    {getMessage('questionSet.questions.textIt')}
                                  </Grid.Column>
                                  <Grid.Column width={11}>
                                    <Field
                                        name={`${name}.textIt`}
                                        component={Input}
                                        placeholder={getMessage('questionSet.questions.text.placeholder')}
                                        fluid
                                    />
                                  </Grid.Column>
                                </Grid.Row>
                                <Grid.Row>
                                  <Grid.Column width={2}/>
                                  <Grid.Column width={3}>
                                    {getMessage('questionSet.questions.details')}
                                  </Grid.Column>
                                  <Grid.Column width={3}>
                                    <Field
                                        name={`${name}.questionType`}
                                        component={Select}
                                        placeholder={`${getMessage('select')} ${getMessage('question.type')}`}
                                        options={questionTypeOptions}
                                        validate={required}
                                        fluid
                                    />
                                  </Grid.Column>
                                  <Grid.Column width={2}>
                                    <Field
                                        name={`${name}.questionKey`}
                                        component={Input}
                                        validate={required}
                                        placeholder={getMessage('questionSet.questions.key.placeholder')}
                                        fluid
                                    />
                                  </Grid.Column>
                                  <Grid.Column width={2}>
                                    <Field
                                        name={`${name}.withRemarks`}
                                        component={CheckBox}
                                        label={getMessage('questionSet.questions.withRemarks')}
                                    />
                                  </Grid.Column>
                                  <Grid.Column width={4}>
                                    <Button compact type="button" onClick={(event) => {
                                      event.preventDefault();
                                      fields.remove(index)
                                    }}>
                                      <StyledIcon name="remove"/>
                                      <span>{getMessage('questionSet.questions.button.remove')}</span>
                                    </Button>
                                  </Grid.Column>
                                </Grid.Row>
                                <Grid.Row>
                                  <Grid.Column width={2}/>
                                  <Grid.Column width={3}>
                                    {getMessage('questionSet.answers')}
                                  </Grid.Column>
                                  <Grid.Column width={11}>
                                    {this.renderQuestionPossibleAnswers(name, currentQuestion)}
                                  </Grid.Column>
                                </Grid.Row>
                              </React.Fragment>
                          )
                        });
                        return (
                            <React.Fragment>
                              {questionsForm}
                              <Grid.Row>
                                <Grid.Column width={5}>
                                  <Button compact type="button" onClick={(event) => {
                                    event.preventDefault();
                                    fields.push({
                                      questionType: "YES_NO",
                                      questionOrder: fields ? fields.length! + 1 : 1
                                    })
                                  }}>
                                    <StyledIcon name="add"/>
                                    <span>{getMessage('questionSet.questions.button.add')}</span>
                                  </Button>
                                </Grid.Column>
                              </Grid.Row>
                            </React.Fragment>
                        );
                      }
                    }
                  </FieldArray>
                </PraxFormGrid>
              </PraxFormScrollableContainer>
            </FormContent>
            <FormButtonsRow>
              <Button compact type="Submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage('button.cancel')}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    )
  }

  renderQuestionPossibleAnswers = (questionPath: string, question: QuestionDto): React.ReactNode => {

    const questionType = question.questionType;
    switch (questionType) {
      case "YES_NO":
        return (
            <>
              {this.props.getMessage('questionSet.answers.boolean.description')}
            </>
        );
      case "SINGLE_ANSWER":
      case "MULTI_ANSWER":
        return (
            <PraxFormGrid>
              <FieldArray name={`${questionPath}.possibleAnswers`}>
                {
                  ({fields}) => (
                      <>
                        {fields.map((name) => (
                            <Grid.Row key={name}>
                              <Grid.Column width={16}>
                                <Field name={`${name}`} component={Input} fluid/>
                              </Grid.Column>
                            </Grid.Row>
                        ))
                        }
                        <Grid.Row>
                          <Grid.Column>
                            <Button compact type="button" onClick={(event) => {
                              event.preventDefault();
                              fields.push("");
                            }}>
                              <StyledIcon name="add"/>
                            </Button>
                          </Grid.Column>
                        </Grid.Row>
                      </>
                  )
                }
              </FieldArray>
            </PraxFormGrid>
        );
      default:
        return (
            <React.Fragment>
              {this.props.getMessage('questionSet.answers.freetext.description')}
            </React.Fragment>
        );
    }
  }

}

export default withMessages(UpsertQuestionSetForm);
